import { useState, useEffect, useCallback, useRef } from 'react';
import { getLegalStatus } from '../utils/legalApi';

interface LegalStatusState {
  isLoading: boolean;
  needsAcceptance: string[];
  currentVersions: Record<string, string>;
  error: string | null;
}

const IDLE_STATE: LegalStatusState = {
  isLoading: false,
  needsAcceptance: [],
  currentVersions: {},
  error: null,
};

export function useLegalStatus({ enabled }: { enabled: boolean }) {
  const [status, setStatus] = useState<LegalStatusState>(() =>
    enabled ? { isLoading: true, needsAcceptance: [], currentVersions: {}, error: null } : IDLE_STATE
  );
  const [invalidateCount, setInvalidateCount] = useState(0);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!enabled || checkedRef.current) return;
    checkedRef.current = true;

    let cancelled = false;

    getLegalStatus()
      .then(data => {
        if (!cancelled) {
          setStatus({
            isLoading: false,
            needsAcceptance: data.needsAcceptance,
            currentVersions: data.currentVersions,
            error: null,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setStatus({
            ...IDLE_STATE,
            error: err instanceof Error ? err.message : 'Failed to load account status',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, invalidateCount]);

  const invalidate = useCallback(() => {
    checkedRef.current = false;
    setStatus({ isLoading: true, needsAcceptance: [], currentVersions: {}, error: null });
    setInvalidateCount(c => c + 1);
  }, []);

  return { ...status, invalidate };
}
